// docs/113: the ground under a quest's path. The stops sit left to right across the chart-sized
// board (the forge's x/y hints), joined by right-angled roads routed from the seed (as the chart's); the board is drawn in the quest's scene:
// out in the open a seeded country of the scene's kit (trees, sand and rock, fen pools, moor crags),
// indoors rooms carved out of the scene's stone round every stop with passages along the roads
// (a castle's grey halls with banners, an inn's cellar on plank floors among barrels and crates, a
// crypt's bones and urns). Pure and deterministic from the instance, so the party sees one map.
import { PLANKS, PROPS } from '../../../game/tiling/oryx';
import type { Scene } from '../../../core/adventure/graph';
import { OW_H, OW_W, type MapProp } from './overworld';

export const QP_W = OW_W, QP_H = OW_H;
/** Cells between one column of stops and the next, and between the lanes of a choice. */
export const COLUMN_CELLS = 4.5;
const LANE_CELLS = 0.11;   // per percent of the forge's y hint from the middle
export interface PathSite { id: number; x: number; y: number; big?: boolean }
export interface PathEdge { a: number; b: number }
export interface PathBoard {
  rows: string[]; fills: MapProp[]; props: MapProp[];
  nodes: Map<number, { x: number; y: number }>;
  /** One road per edge (same order), cell coordinates through cell centres. */
  paths: Array<Array<[number, number]>>;
  kit: string; indoor: boolean;
}

/** The view kit a scene is drawn in (out in the open the season dresses plain green woods). */
export function sceneKit(scene: Scene, season?: string): string {
  switch (scene) {
    case 'Keep': return 'keep';
    case 'Cistern': return 'cistern';
    case 'Crypt': return 'crypt';
    case 'Sewer': return 'sewer';
    case 'Cellar': return 'brick';
    case 'Shrine': return 'shrine';
    case 'Tomb': return 'tomb';
    case 'Cavern': return 'cavern';
    case 'Temple': return 'temple';
    case 'Bog': return 'mirewood';
    case 'Autumnwood': return 'autumn';
    case 'Pinewood': return 'pinewood';
    case 'Snowwood': return 'winterwood';
    case 'Desert': return 'dunes';
    case 'Moor': return 'highland';
    case 'Jungle': return 'jungle';
    case 'Underdark': return 'underdark';
    case 'Ashlands': return 'ashlands';
    case 'Greenwood': return season === 'winter' ? 'winterwood' : season === 'autumn' ? 'autumn' : 'forest';
  }
}

export const INDOOR_SCENES: ReadonlySet<Scene> = new Set(['Keep', 'Cistern', 'Crypt', 'Sewer', 'Cellar', 'Shrine', 'Tomb', 'Cavern', 'Temple']);

/** A scene as words, for the map's plate ("an inn's cellar", "the open desert"). */
export const SCENE_WORDS: Record<Scene, string> = {
  Keep: 'castle halls', Cistern: 'a flooded vault', Crypt: 'a crypt', Sewer: 'the sewers', Cellar: 'a cellar',
  Shrine: 'a shrine', Tomb: 'a tomb', Cavern: 'caves', Temple: 'a sandstone temple', Bog: 'the fens',
  Greenwood: 'the woods', Autumnwood: 'autumn woods', Pinewood: 'pine woods', Snowwood: 'the snows',
  Desert: 'the open desert', Moor: 'the moors', Jungle: 'the jungle', Underdark: 'the Underdark',
  Ashlands: 'the Ashlands',
};

function hash(seed: number, x: number, y: number, salt: number): number {
  let h = (seed ^ Math.imul(x + 31, 0x9e3779b1) ^ Math.imul(y + 17, 0x85ebca6b) ^ Math.imul(salt, 0xc2b2ae35)) >>> 0;
  h ^= h >>> 15; h = Math.imul(h, 0x2c1b3c6d); h ^= h >>> 12; h = Math.imul(h, 0x297a2d39); h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}
/** Smooth value noise on a lattice of `step` cells. */
function noise(seed: number, salt: number, step: number) {
  const s = (t: number) => t * t * (3 - 2 * t);
  return (x: number, y: number) => {
    const fx = x / step, fy = y / step, i = Math.floor(fx), j = Math.floor(fy), u = s(fx - i), v = s(fy - j);
    const g = (a: number, b: number) => hash(seed, a, b, salt);
    return (g(i, j) * (1 - u) + g(i + 1, j) * u) * (1 - v) + (g(i, j + 1) * (1 - u) + g(i + 1, j + 1) * u) * v;
  };
}
const pick = <T,>(v: readonly T[], r: number): T => v[Math.min(v.length - 1, Math.floor(r * v.length))]!;

/** Where each stop sits: its column (the forge's x hints, in order) `COLUMN_CELLS` apart about the board's
 *  middle, its lane (top, middle, bottom) by its y hint. */
export function placeStops(sites: readonly PathSite[]): Map<number, { x: number; y: number }> {
  const xs = [...new Set(sites.map((s) => s.x))].sort((a, b) => a - b);
  const nodes = new Map<number, { x: number; y: number }>();
  for (const s of sites) {
    const col = xs.indexOf(s.x) - (xs.length - 1) / 2;
    nodes.set(s.id, { x: Math.round(QP_W / 2 + col * COLUMN_CELLS) + 0.5, y: Math.round(QP_H / 2 + (s.y - 50) * LANE_CELLS) + 0.5 });
  }
  return nodes;
}

/** The cells (stage px at `cell`) that hold every stop with its labels: what the camera frames. */
export function pathFrame(nodes: Map<number, { x: number; y: number }>, cell: number): { x0: number; y0: number; x1: number; y1: number } {
  const v = [...nodes.values()];
  return { x0: (Math.min(...v.map((n) => n.x)) - 2.6) * cell, x1: (Math.max(...v.map((n) => n.x)) + 2.6) * cell, y0: (Math.min(...v.map((n) => n.y)) - 2.4) * cell, y1: (Math.max(...v.map((n) => n.y)) + 2.2) * cell };
}

/** A road from one stop to the next, in right angles as the chart's roads run (docs/113 QP10): level out
 *  of the stop, then a seeded route of one or two jogs at whole cells between the columns, level into
 *  the next stop. A road between stops in one lane sometimes steps aside and back. `r` is the road's own
 *  roll in [0, 1). The corners sit on cell centres, so every leg runs straight along a row or a column.
 *  `avoid` holds the columns other stops' roads already climb in, so crossing roads keep apart where they can. */
export function roadBetween(a: { x: number; y: number }, b: { x: number; y: number }, r = 0.5, avoid: ReadonlySet<number> = new Set()): Array<[number, number]> {
  const roll = (salt: number) => hash(Math.floor(r * 4294967296), salt, 0, 21);
  // the columns a jog may stand in: whole cells (on cell centres) at least one cell from either stop
  const lo = Math.ceil(a.x - 0.5) + 1.5, hi = Math.floor(b.x - 0.5) - 0.5;
  const every = hi >= lo ? Array.from({ length: Math.floor(hi - lo) + 1 }, (_, i) => lo + i) : [];
  const free = every.filter((x) => !avoid.has(x));
  const cols = free.length ? free : every;
  if (!cols.length) return Math.abs(a.y - b.y) < 0.01 ? [[a.x, a.y], [b.x, b.y]] : [[a.x, a.y], [a.x, b.y], [b.x, b.y]];
  const out: Array<[number, number]> = [[a.x, a.y]];
  const via = (x: number, y: number) => { const l = out[out.length - 1]!; if (l[0] !== x || l[1] !== y) out.push([x, y]); };
  const level = Math.abs(a.y - b.y) < 0.01;
  if (level && (cols.length < 2 || roll(1) < 0.5)) return [[a.x, a.y], [b.x, b.y]];
  const span = Math.round(Math.abs(b.y - a.y));
  if (level || (cols.length >= 2 && span >= 2 && roll(2) < 0.45)) {
    // two jogs: out to a first column, across a lane of its own, on to a second column, into the stop.
    // Between two lanes the middle one stays between them, so the roads of a choice never cross.
    const i = Math.floor(roll(3) * (cols.length - 1)), j = i + 1 + Math.floor(roll(4) * (cols.length - 1 - i));
    const j1 = cols[i]!, j2 = cols[j]!;
    const mid = level ? a.y + (roll(5) < 0.5 ? -1 : 1) * (1 + Math.floor(roll(6) * 2))
      : a.y + Math.sign(b.y - a.y) * (1 + Math.floor(roll(6) * (span - 1)));
    via(j1, a.y); via(j1, mid); via(j2, mid); via(j2, b.y);
  } else {
    // one jog, at a seeded column
    const j = cols[Math.floor(roll(3) * cols.length)]!;
    via(j, a.y); via(j, b.y);
  }
  via(b.x, b.y);
  // a corner only where the road turns
  return out.filter((p, i) => {
    const q = out[i - 1], n = out[i + 1];
    return !q || !n || !((q[0] === p[0] && p[0] === n[0]) || (q[1] === p[1] && p[1] === n[1]));
  });
}

/** Every cell a polyline passes through. */
function cellsOf(path: ReadonlyArray<readonly [number, number]>): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let i = 0; i < path.length - 1; i++) {
    const [ax, ay] = path[i]!, [bx, by] = path[i + 1]!;
    const n = Math.max(1, Math.ceil(Math.max(Math.abs(bx - ax), Math.abs(by - ay)) * 2));
    for (let s = 0; s <= n; s++) out.push([Math.floor(ax + ((bx - ax) * s) / n), Math.floor(ay + ((by - ay) * s) / n)]);
  }
  return out;
}

export function generatePath(seed: number, scene: Scene, season: string | undefined, sites: readonly PathSite[], edges: readonly PathEdge[]): PathBoard {
  const kit = sceneKit(scene, season);
  const indoor = INDOOR_SCENES.has(scene);
  const nodes = placeStops(sites);
  // the columns each stop's roads climb in, so another stop's road climbs elsewhere
  const climbs = new Map<number, Set<number>>();
  const paths = edges.map((e) => {
    const a = nodes.get(e.a), b = nodes.get(e.b);
    if (!a || !b) return [];
    const avoid = new Set([...climbs].filter(([from]) => from !== e.a).flatMap(([, xs]) => [...xs]));
    const p = roadBetween(a, b, hash(seed, e.a, e.b, 20), avoid);
    const mine = climbs.get(e.a) ?? new Set<number>();
    for (let i = 0; i < p.length - 1; i++) if (p[i]![0] === p[i + 1]![0]) mine.add(p[i]![0]);
    climbs.set(e.a, mine);
    return p;
  });
  const big = new Set(sites.filter((s) => s.big).map((s) => s.id));
  return indoor ? carveHalls(seed, scene, kit, nodes, paths, big) : openCountry(seed, scene, kit, nodes, paths);
}

// ---- out in the open ------------------------------------------------------------------------

function openCountry(seed: number, scene: Scene, kit: string, nodes: Map<number, { x: number; y: number }>, paths: Array<Array<[number, number]>>): PathBoard {
  const W = QP_W, H = QP_H;
  const grid: string[][] = Array.from({ length: H }, () => Array.from({ length: W }, () => '.'));
  const k = (x: number, y: number) => `${x},${y}`;
  const keep = new Set<string>();
  for (const n of nodes.values()) for (let dy = -2; dy <= 1; dy++) for (let dx = -2; dx <= 2; dx++) keep.add(k(Math.floor(n.x) + dx, Math.floor(n.y) + dy));
  for (const p of paths) for (const [x, y] of cellsOf(p)) keep.add(k(x, y));
  const props: MapProp[] = [];
  const fills: MapProp[] = [];
  const tn = noise(seed, 1, 4), mn = noise(seed, 2, 3), pn = noise(seed, 3, 3);
  // how thick the country grows: woods close in, the moor and the desert lie open
  const wall = { Greenwood: 0.58, Autumnwood: 0.58, Pinewood: 0.55, Snowwood: 0.6, Jungle: 0.52, Bog: 0.64, Moor: 0.7, Desert: 0.74, Underdark: 0.6, Ashlands: 0.7 }[scene as string] ?? 0.6;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    if (keep.has(k(x, y))) continue;
    const edge = Math.min(x, W - 1 - x, y, H - 1 - y) < 2 ? 0.14 : 0;
    const t = tn(x, y) + edge, m = mn(x, y), r = hash(seed, x, y, 9);
    if (scene === 'Bog' && m > 0.6) { grid[y]![x] = m > 0.73 ? '~' : ','; continue; }
    if (scene === 'Jungle' && m > 0.7) { grid[y]![x] = '~'; continue; }
    // docs/23 Y9: black pools and the abyss break the Underdark's floor
    if (scene === 'Underdark' && m > 0.72) { grid[y]![x] = m > 0.8 ? '_' : '~'; continue; }
    // docs/23 Y10: the Ashlands' water is lava (the kit's water set), in flows and pools across the black rock
    if (scene === 'Ashlands' && m > 0.74) { grid[y]![x] = '~'; continue; }
    if (t > wall) { grid[y]![x] = 'T'; continue; }
    if (pn(x, y) > 0.68 && r > 0.35) {
      const frame = scene === 'Desert' ? pick([...PROPS.cacti, ...PROPS.rocksOrange], r)
        : scene === 'Moor' ? pick([...PROPS.rocksBrown, ...PROPS.shrubs.tufts], r)
        : scene === 'Snowwood' ? pick(PROPS.trees.snowPineSingle, r)
        : scene === 'Pinewood' ? pick(PROPS.trees.pineSingle, r)
        : scene === 'Autumnwood' || kit === 'autumn' ? pick(PROPS.trees.autumnSingle, r)
        : scene === 'Jungle' ? pick([...PROPS.roundBush, PROPS.trees.fruit], r)
        : scene === 'Underdark' ? pick([...PROPS.starPlant, ...PROPS.shrubs.teal, PROPS.crystals.grey[0]], r)
        : scene === 'Ashlands' ? pick([...PROPS.rocksDarkGrey, PROPS.trees.dead, PROPS.crystals.orange], r)
        : scene === 'Bog' ? pick([...PROPS.shrubs.teal, ...PROPS.lilyPad], r)
        : kit === 'winterwood' ? pick(PROPS.trees.snowPineSingle, r)
        : pick(PROPS.trees.oakSingle, r);
      props.push({ frame, x, y });
    } else if (scene === 'Desert' && m > 0.66) grid[y]![x] = ',';
    else if (scene === 'Moor' && m > 0.7) grid[y]![x] = ',';
    else if (scene === 'Ashlands' && m > 0.64) grid[y]![x] = ',';
  }
  props.sort((a, b) => a.y - b.y || a.x - b.x);
  return { rows: grid.map((r) => r.join('')), fills, props, nodes, paths, kit, indoor: false };
}

// ---- indoors --------------------------------------------------------------------------------

/** What a scene's rooms hold, by the wall (`edge`) and out on the floor (`floor`). */
const FURNISH: Partial<Record<Scene, { edge: readonly number[]; floor: readonly number[]; rate: number }>> = {
  Keep: { edge: [PROPS.bannerRed[0], PROPS.bannerBlue[0], PROPS.statues.knight, PROPS.furniture.bookshelf, PROPS.brazier[0]], floor: [PROPS.furniture.tableCandles[0], PROPS.furniture.stool], rate: 0.32 },
  Cellar: { edge: [...PROPS.barrel, ...PROPS.crate, PROPS.furniture.shelf, ...PROPS.bucket], floor: [...PROPS.barrel, PROPS.crate[0]], rate: 0.5 },
  Crypt: { edge: [...PROPS.urns.grey, PROPS.tombstone, PROPS.statues.lying], floor: [...PROPS.bones, PROPS.skull], rate: 0.4 },
  Tomb: { edge: [PROPS.statues.lying, PROPS.tombstone, PROPS.skullPile, ...PROPS.urns.grey], floor: [...PROPS.bones, ...PROPS.bonePiles], rate: 0.4 },
  Sewer: { edge: [...PROPS.pipes.grey.slice(0, 3), PROPS.rubble], floor: [...PROPS.slime, ...PROPS.puddle], rate: 0.3 },
  Cistern: { edge: [...PROPS.urns.blue, PROPS.statues.hooded], floor: [...PROPS.puddle], rate: 0.3 },
  Cavern: { edge: [PROPS.crystals.orange, ...PROPS.crystals.grey, PROPS.boulder], floor: [PROPS.rocks, PROPS.rubble], rate: 0.35 },
  Temple: { edge: [...PROPS.urns.orange, PROPS.pedestals.altar, PROPS.statues.hooded, PROPS.brazier[0]], floor: [PROPS.hornedSkull, PROPS.paleRubble], rate: 0.35 },
  Shrine: { edge: [PROPS.pedestals.fountain, PROPS.statues.hooded, ...PROPS.urns.green], floor: [...PROPS.flowers], rate: 0.3 },
};

function carveHalls(seed: number, scene: Scene, kit: string, nodes: Map<number, { x: number; y: number }>, paths: Array<Array<[number, number]>>, big: Set<number>): PathBoard {
  const W = QP_W, H = QP_H;
  const grid: string[][] = Array.from({ length: H }, () => Array.from({ length: W }, () => 'T'));
  const open = (x: number, y: number, c = '.') => { if (x > 0 && y > 0 && x < W - 1 && y < H - 1) grid[y]![x] = c; };
  // a room round every stop (the boss's and the gate's larger), a little uneven
  const rooms: Array<{ x0: number; y0: number; x1: number; y1: number }> = [];
  for (const [id, n] of nodes) {
    const cx = Math.floor(n.x), cy = Math.floor(n.y);
    const r = hash(seed, cx, cy, 5);
    const hw = big.has(id) ? 3 : 2 + (r > 0.6 ? 1 : 0), up = big.has(id) ? 3 : 2, down = big.has(id) ? 2 : 1 + (r > 0.3 ? 1 : 0);
    const room = { x0: cx - hw, y0: cy - up, x1: cx + hw, y1: cy + down };
    rooms.push(room);
    for (let y = room.y0; y <= room.y1; y++) for (let x = room.x0; x <= room.x1; x++) open(x, y);
  }
  // passages along the roads (two wide where they run level, so the band has room)
  for (const p of paths) for (const [x, y] of cellsOf(p)) { open(x, y); if (scene !== 'Cavern') open(x, y + 1); }
  // a cave's walls are ragged: nibble its rooms' edges
  if (scene === 'Cavern') {
    const nn = noise(seed, 7, 2);
    for (const rm of rooms) for (let y = rm.y0 - 1; y <= rm.y1 + 1; y++) for (let x = rm.x0 - 1; x <= rm.x1 + 1; x++) if (nn(x, y) > 0.62) open(x, y);
  }
  // a sewer's channel and a cistern's pools of standing water (never on a road)
  const road = new Set<string>();
  for (const p of paths) for (const [x, y] of cellsOf(p)) road.add(`${x},${y}`);
  for (const n of nodes.values()) for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) road.add(`${Math.floor(n.x) + dx},${Math.floor(n.y) + dy}`);
  if (scene === 'Sewer' || scene === 'Cistern') {
    const wn = noise(seed, 8, 3);
    for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) if (grid[y]![x] === '.' && !road.has(`${x},${y}`) && wn(x, y) > 0.6) grid[y]![x] = '~';
  }
  // furnishings: against the walls, a few out on the floor, none on a road or a stop
  const props: MapProp[] = [];
  const f = FURNISH[scene];
  if (f) for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) {
    if (grid[y]![x] !== '.' || road.has(`${x},${y}`)) continue;
    const r = hash(seed, x, y, 11);
    const byWall = grid[y - 1]![x] === 'T';
    if (byWall && r < f.rate) props.push({ frame: pick(f.edge, hash(seed, x, y, 12)), x, y });
    else if (!byWall && r < f.rate * 0.18) props.push({ frame: pick(f.floor, hash(seed, x, y, 13)), x, y });
  }
  // an inn's cellar has plank floors
  const fills: MapProp[] = [];
  if (scene === 'Cellar') for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (grid[y]![x] === '.') fills.push({ frame: PLANKS.ew[(x + y * 2) % 3]!, x, y });
  props.sort((a, b) => a.y - b.y || a.x - b.x);
  return { rows: grid.map((r) => r.join('')), fills, props, nodes, paths, kit, indoor: true };
}

/** The road a band walks from one stop to the next (the edge's own road), or none. */
export function pathRoute(board: Pick<PathBoard, 'paths'>, edges: readonly PathEdge[], from: number, to: number): Array<[number, number]> | undefined {
  const i = edges.findIndex((e) => e.a === from && e.b === to);
  const p = board.paths[i];
  return p && p.length >= 2 ? p.slice() : undefined;
}
